import { ActionTree } from 'vuex';
import { GlobalState } from './types';
import { RootState } from '../../types';
import * as types from './mutations-types';
import TinabaAPI from '@/common/TinabaAPI'
import Cookies from 'js-cookie'
import { loadPaypalSDK, processAlipayCheckout } from '@/common/utils'
import { resetCartInSession, resetSession } from './mutations'
import GTMService from '@/services/GTMService';

const API = new TinabaAPI()

function getCartFromSession () {
  try {
    return Cookies.get('CART_ID')
  } catch (error) {
    return null
  }
}

export const actions: ActionTree<GlobalState, RootState> = {

  async toggleHeader ({ commit }, status = false) {
    await commit(types.toggleHeader, status);
  },

  async toggleCart ({ commit }, status = false) {
    await commit(types.toggleCart, status);
  },

  async fetchCart ({ commit, rootGetters }) {
    if (!rootGetters['vas/merchant']) return null
    commit('toggleGlobalLoading', true)
    const { id } = rootGetters['vas/merchant']
    const cartId = getCartFromSession()
    let cart = null
    try {
      if (cartId) {
        try {
          cart = await API.$vas.getCart(id, cartId)
        } catch (error) {
          cart = await API.$vas.createCart(id, { products: [] })
        }
      } else {
        cart = await API.$vas.createCart(id, { products: [] })
      }
      commit(types.setCart, cart)
    } catch (error) {}
    finally {
      commit('toggleGlobalLoading', false)
    }
  },

  async addToCart ({ commit, state, rootGetters }, payload) {
    if (!state.cart) return alert('NO Cart Found')
    const { id } = rootGetters['vas/merchant']
    let updating = false
    if (Array.isArray(state.cart.products) && state.cart.products.length) {
      const itemInCart = state.cart.products.find(product => product.id === payload.product_id)

      if (itemInCart) {
        updating = true
      }
    }
    try {
      commit('toggleGlobalLoading', true)
      const cart = await API.$vas.cartAddUpdateItem(id, state.cart.id, payload.product_id, payload.quantity, !updating)
      commit(types.setCart, cart)

      // Send GTM dataLayer
      const product = state.cart.products.find(product => product.id === payload.product_id) as MerchantCartItem;
      if(payload.prevQuantity > payload.quantity)
      {
        GTMService.dataLayerRemoveFromCart(product, rootGetters['vas/merchant']);
      }
      else
      {
        GTMService.dataLayerAddToCart(product, rootGetters['vas/merchant'], payload.quantity);
      }

    } catch (error) {
      console.log('Error: Add to Cart', error)
    } finally {
      commit('toggleGlobalLoading', false)
    }
  },

  async removeFromCart ({ commit, state, rootGetters }, productId) {
    if (!state.cart || !state.cart.products) return alert('No Cart Found')
    const { id } = rootGetters['vas/merchant']
    try {
      commit('toggleGlobalLoading', true)
      const item = state.cart.products.find(i => productId === i.id)
      if (item) {
        const cart = await API.$vas.cartRemoveItem(id, state.cart.id, productId, item.requestedQuantity)
        commit(types.setCart, cart)

        // Send GTM dataLayer
        GTMService.dataLayerRemoveFromCart(item, rootGetters['vas/merchant']);
      }
    } catch (error) {
      console.log('Error: Remove from Cart', error)
    } finally {
      commit('toggleGlobalLoading', false)
    }
  },

  async saveCustomerDetailsToCart ({ commit, state, rootGetters, dispatch }, retry = true) {
    if (!state.cart) return alert('No Cart Found')
    const { id } = rootGetters['vas/merchant']
    try {
      commit('toggleGlobalLoading', true)
      const customerDetails = rootGetters['vas/customerDetails']
      const address = rootGetters['vas/shippingAddress']
      if (address) {
        const cart = await API.$vas.cartUpdateAddress(id, state.cart.id, address, 'shipping', customerDetails.email, customerDetails.fiscalCode, customerDetails.pickupPoint, customerDetails.message)
        commit(types.setCart, cart)
      } else {
        const customerDetails = rootGetters['vas/customerDetails']
        if (customerDetails) {
          const cart = await API.$vas.cartUpdateAddress(id, state.cart.id, customerDetails, 'billing', customerDetails.email, customerDetails.fiscalCode, customerDetails.pickupPoint, customerDetails.message)
          commit(types.setCart, cart)
        }
      }

    } catch (error) {
      if (retry) {
        resetCartInSession()
        await dispatch('fetchCart')
        await dispatch('saveCustomerDetailsToCart', false)
      }
      console.log('Error: Save Address to Cart, try reloading', error)
      throw error
    } finally {
      commit('toggleGlobalLoading', false)
    }
  },

  async initCheckout ({ commit, getters, rootGetters, rootState, state, dispatch }) {
    if (!state.cart) return alert('No Cart Found')
    const { businessId, shopId, configuration, id } = rootGetters['vas/merchant']
    try {
      const selectedTimeslot = rootState.vas.selectedTimeslot
      commit('global/toggleGlobalLoading', true, { root: true })
      const checkout = await API.$vas.initCheckout(id, state.cart.id, `${window.location.origin}/${businessId}/public/${shopId}/${id}/payment`, selectedTimeslot ? selectedTimeslot : undefined)
      commit(types.setCheckout, checkout)
    } catch (error) {

    } finally {
      commit('toggleGlobalLoading', false)
    }
  },

  async resetCheckout ({ commit }) {
    commit(types.setCheckout, null)
  },

  async fetchCheckoutWithID ({ commit, dispatch }, { id, isRedirect = false }: any) {
    try {
      commit('global/toggleGlobalLoading', true, { root: true })
      const checkout = await API.$vas.getVasOrder(id)
      commit(types.setCheckout, checkout)
      if (!isRedirect) {
        commit('global/toggleGlobalLoading', false, { root: true })
      } else {
        commit('global/toggleGlobalLoading', true, { root: true })
      }
    } catch (error) {
      console.log(error)
      if (error.status === 410) {
        // Checkout Completed
        commit(types.setCheckout, error.data)
        if (error.data.status === 'PAYMENT_SUCCESS') {
          commit('global/toggleGlobalLoading', false, { root: true })
        }
        if (error.data.status === 'PAYMENT_STARTED') {
          setTimeout(() => {
            dispatch('fetchCheckoutWithID', { id, isRedirect: true })
          }, 3000)
        }
      }
    } finally {

    }
  },

  async triggerCheckoutPoll ({ commit, state, dispatch }) {
    if (!state.checkout) return console.log('No Checkout Found')
    try {
      const checkout = await API.$vas.getVasOrder(state.checkout.id)
      commit(types.setCheckout, checkout)
    } catch (error) {
      if (error.status === 410) {
        // Checkout Completed
        commit(types.setCheckout, error.data)
        if (error.data.status === 'PAYMENT_SUCCESS') {
          commit('global/toggleGlobalLoading', false, { root: true })
        }
        if (error.data.status === 'PAYMENT_STARTED') {
          commit('global/toggleGlobalLoading', true, { root: true })
          setTimeout(() => {
            dispatch('triggerCheckoutPoll')
          }, 2000)
        }
      }
      throw error
    } finally {}
  },

  async startAliPayCheckout ({ commit, state }) {
    try {
      if (state.checkout) {
        commit('global/toggleGlobalLoading', true, { root: true })
        const data = await API.$vas.createAlipayCheckout(state.checkout.id)
        await processAlipayCheckout(data, state.checkout.id)
      }
    } catch (error) {
      commit('global/toggleGlobalLoading', false, { root: true })
      console.error('err alipay checkout', error);
      alert(error);
      throw new Error(error)
    }
    // We do not restore loader as there needs to be a redirect
  },

  async fetchPaypalDetails ({ commit, rootGetters }) {
    try {
      const { businessId } = rootGetters['vas/merchant']
      const data = await API.$vas.fetchPaypalData(businessId)
      await loadPaypalSDK(data)
      await commit(types.setPaypalDetails, data)
    } catch (error) {
      console.log('err fetchPaypalDetails', error);
    }
  }
};
